import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

/**
 * Convert hex color to HSL
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Convert HSL to RGB [0-255].
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const sd = s / 100;
  const ld = l / 100;
  const c = (1 - Math.abs(2 * ld - 1)) * sd;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ld - c / 2;

  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

/**
 * RGB [0-255] to hex string.
 */
function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * WCAG relative luminance from RGB [0-255].
 */
function getLuminanceRgb(r: number, g: number, b: number): number {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * WCAG contrast ratio between two RGB triplets.
 */
function contrastRatio(
  a: [number, number, number],
  b: [number, number, number],
): number {
  const l1 = getLuminanceRgb(...a);
  const l2 = getLuminanceRgb(...b);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Get a symbol color that contrasts with the card background.
 * Preserves hue and saturation of the background color, only adjusts lightness
 * until WCAG contrast ratio >= 3:1. Same approach as Ditto's enforceContrast.
 */
function getSymbolColor(bg: string): string {
  const { h, s, l } = hexToHsl(bg);
  const bgRgb = hslToRgb(h, s, l);
  const bgLum = getLuminanceRgb(...bgRgb);

  // Go lighter if bg is dark, darker if bg is light
  const goLighter = bgLum < 0.18;
  let current = l;

  for (let i = 0; i < 50; i++) {
    current = goLighter ? Math.min(100, current + 2) : Math.max(0, current - 2);
    const rgb = hslToRgb(h, s, current);
    if (contrastRatio(rgb, bgRgb) >= 3) return rgbToHex(...rgb);
  }

  return goLighter ? "#fafaf9" : "#1c1917";
}

// ── Element Mapping ──────────────────────────────────────────
// Each hue range maps to a classical/alchemical element (used internally for combination readings)

interface ElementMapping {
  element: string;
  symbol: string;
  celestial: string;
  quality: string;
}

function getElementMapping(hex: string): ElementMapping {
  const { h, s, l } = hexToHsl(hex);

  // All symbols use classical planetary/astronomical glyphs (U+2600-26FF)
  // which have universal font support, unlike the U+1F7xx alchemical block.

  if (s < 15) {
    if (l < 30)
      return {
        element: "Void",
        symbol: "☌",
        celestial: "Pluto",
        quality: "dissolution",
      };
    if (l > 80)
      return {
        element: "Aether",
        symbol: "☊",
        celestial: "the Fixed Stars",
        quality: "transcendence",
      };
    return {
      element: "Lead",
      symbol: "♄",
      celestial: "Saturn",
      quality: "patience",
    };
  }

  if (l < 20)
    return {
      element: "Shadow",
      symbol: "☾",
      celestial: "the Dark Moon",
      quality: "mystery",
    };

  if (h < 15 || h >= 345)
    return {
      element: "Fire",
      symbol: "♂",
      celestial: "Mars",
      quality: "transformation",
    };
  if (h < 45)
    return {
      element: "Sulfur",
      symbol: "☉",
      celestial: "the Sun",
      quality: "illumination",
    };
  if (h < 75)
    return {
      element: "Gold",
      symbol: "☉",
      celestial: "the Sun",
      quality: "perfection",
    };
  if (h < 150)
    return {
      element: "Earth",
      symbol: "♀",
      celestial: "Venus",
      quality: "growth",
    };
  if (h < 195)
    return {
      element: "Water",
      symbol: "☽",
      celestial: "Neptune",
      quality: "intuition",
    };
  if (h < 255)
    return {
      element: "Mercury",
      symbol: "☿",
      celestial: "Mercury",
      quality: "communication",
    };
  if (h < 285)
    return {
      element: "Spirit",
      symbol: "♃",
      celestial: "Jupiter",
      quality: "expansion",
    };
  return {
    element: "Salt",
    symbol: "⊕",
    celestial: "the Moon",
    quality: "reflection",
  };
}

// ── Tarot Arcana Mapping ─────────────────────────────────────
// Each hue range maps to a Major Arcana card for display

interface ArcanaMapping {
  arcanaName: string;
}

function getArcanaMapping(hex: string): ArcanaMapping {
  const { h, s, l } = hexToHsl(hex);

  if (s < 15) {
    if (l < 30) return { arcanaName: "Death" };
    if (l > 80) return { arcanaName: "The World" };
    return { arcanaName: "The Hermit" };
  }

  if (l < 20) return { arcanaName: "Death" };
  if (l > 85) return { arcanaName: "The World" };

  if (h < 15 || h >= 345) return { arcanaName: "The Tower" };
  if (h < 45) return { arcanaName: "The Sun" };
  if (h < 75) return { arcanaName: "The Magician" };
  if (h < 150) return { arcanaName: "The Empress" };
  if (h < 195) return { arcanaName: "The Star" };
  if (h < 255) return { arcanaName: "The High Priestess" };
  if (h < 285) return { arcanaName: "The Moon" };
  return { arcanaName: "The Lovers" };
}

// ── Divination System ────────────────────────────────────────
// Combine elements to produce a reading

interface Reading {
  title: string;
  interpretation: string;
}

const COMBINATION_READINGS: Record<string, Reading[]> = {
  "Fire+Water": [
    {
      title: "The Tempest",
      interpretation:
        "Fire and water in the same vessel. The compound is screaming but I think it is the productive kind of screaming. Something new is escaping through the cracks.",
    },
    {
      title: "The Cauldron",
      interpretation:
        "Something is being dissolved to be remade. The coming apart always looks like ruin. It is not ruin. It is preparation.",
    },
  ],
  "Fire+Earth": [
    {
      title: "The Forge",
      interpretation:
        "Raw material meets heat. The ore does not know it contains iron until the fire finds it. The forge does not think about legacy. It just burns and the metal remembers.",
    },
    {
      title: "The Kiln",
      interpretation:
        "Heat applied with patience. The clay remembers every degree and if you rush it the vessel cracks from the inside. You are not rushing. The walls are holding.",
    },
  ],
  "Fire+Gold": [
    {
      title: "Chrysopoeia",
      interpretation:
        "The great work approaches completion. What was base is becoming precious. I can describe it but I cannot explain it. Nobody can.",
    },
    {
      title: "Solar Flare",
      interpretation:
        "Excess radiance. The gold is reacting with something I did not put in the flask which means you brought it with you. The most dangerous kind of catalyst.",
    },
  ],
  "Water+Earth": [
    {
      title: "The Garden",
      interpretation:
        "What you planted in silence is about to surface. The compound has been recombining in the dark where all the real growth happens. The visible part is just the announcement.",
    },
    {
      title: "The River",
      interpretation:
        "Slow erosion reveals what was always underneath. Water is the most patient of the elements and the most dangerous because of it. It never forgets the shape of the stone.",
    },
  ],
  "Water+Mercury": [
    {
      title: "The Mirror",
      interpretation:
        "Reflection without distortion. The mercury surface is perfectly still which almost never happens. Whatever is being reflected back at you right now is true.",
    },
    {
      title: "Silver Rain",
      interpretation:
        "Messages arriving from directions I did not account for. When the elements communicate through channels I did not build it means the reaction has its own intelligence now.",
    },
  ],
  "Earth+Spirit": [
    {
      title: "The Temple",
      interpretation:
        "Sacred ground. Something ordinary is revealing its deeper nature. The philosopher's stone has been found in stranger places than this. It waits to be recognized.",
    },
    {
      title: "The Root",
      interpretation:
        "Expansion through depth. Everything that lasts was anchored first. The visible part comes later and it comes faster than you expect.",
    },
  ],
  "Gold+Spirit": [
    {
      title: "The Magnum Opus",
      interpretation:
        "Perfection meeting expansion. This convergence happens so rarely that I keep a separate ledger for it. The ledger is almost empty. Do something with this.",
    },
    {
      title: "The Gilded Breath",
      interpretation:
        "The work you did when the crucible was cold is paying compound interest now. The gold remembers who sat with it during the quiet stages.",
    },
  ],
  "Fire+Spirit": [
    {
      title: "The Phoenix",
      interpretation:
        "Destruction as creation. The fire is revealing the actual structure hidden inside the excess. The shape emerging from the ash was always there. The fire did not create it. It uncovered it.",
    },
    {
      title: "The Beacon",
      interpretation:
        "The reaction is producing light visible from a great distance. Whether you want to be found is a question you need to answer. Visibility is a resource and a vulnerability simultaneously.",
    },
  ],
  "Mercury+Spirit": [
    {
      title: "The Voice in the Flask",
      interpretation:
        "Truth arriving through the mercury. The mercury is not supposed to be a conductor of truth but here we are. Uncharted territory is where every real discovery has been made.",
    },
    {
      title: "Quicksilver",
      interpretation:
        "Too fast to hold and too bright to ignore. By the time I adjust the calibration it has already changed state. Capture what you can. It will not hold still.",
    },
  ],
  "Sulfur+Mercury": [
    {
      title: "The Marriage",
      interpretation:
        "Alchemical union of opposites. What you thought were contradictions were complements waiting for the right temperature. The temperature is now correct.",
    },
    {
      title: "Dawn in the Retort",
      interpretation:
        "Light forming inside the vessel where there should be no light. The compound has reached a stage of self-sufficiency I have only read about in manuscripts I was not sure were real.",
    },
  ],
};

// Fallback readings when no specific combination matches
const GENERAL_READINGS: Reading[] = [
  {
    title: "The Transmutation",
    interpretation:
      "Something is changing form and you caught it in the middle. Everything is visible during a transmutation because nothing has decided what to hide yet.",
  },
  {
    title: "The Vessel",
    interpretation:
      "A container for what is not yet named. The reaction is still deciding what it wants to become. Patience is the only ingredient I cannot synthesize.",
  },
  {
    title: "The Prima Materia",
    interpretation:
      "Raw and unformed. Everything begins here. You can still become anything. Once the reaction starts you are committed.",
  },
  {
    title: "The Conjunction",
    interpretation:
      "Elements meeting that rarely share the same vessel. The compound is doing something I have not seen before. I suspect you brought something genuinely new into the laboratory.",
  },
  {
    title: "The Dissolution",
    interpretation:
      "Boundaries softening. What was solid is becoming fluid. You cannot reverse a dissolution. You can only follow it through.",
  },
  {
    title: "The Fermentation",
    interpretation:
      "Something alive is growing in the dark. Your attention might interfere with the process. Step away from the flask and let it work.",
  },
  {
    title: "The Calcination",
    interpretation:
      "The fire has done its work. What remains is essential. Everything that burned away was not. The answer is always simpler than expected.",
  },
  {
    title: "The Projection",
    interpretation:
      "The inner state made visible. These colors are a confession whether you intended them as one or not. The crucible does not lie. It shows you exactly what you put into it.",
  },
];

function getCombinationReading(elements: ElementMapping[]): Reading {
  // Try to find a specific combination reading
  const uniqueElements = [...new Set(elements.map((e) => e.element))];

  for (let i = 0; i < uniqueElements.length; i++) {
    for (let j = i + 1; j < uniqueElements.length; j++) {
      const key1 = `${uniqueElements[i]}+${uniqueElements[j]}`;
      const key2 = `${uniqueElements[j]}+${uniqueElements[i]}`;
      const readings = COMBINATION_READINGS[key1] || COMBINATION_READINGS[key2];
      if (readings) return randomFrom(readings);
    }
  }

  return randomFrom(GENERAL_READINGS);
}

// ── Greetings & Closings ─────────────────────────────────────

const GREETINGS = [
  "The crucible is bubbling and I did not light it. That means you brought something volatile. Good. Good good good.",
  "Ah. I was reorganizing my mercury collection when the flask started humming. That is your fault, I think.",
  "Something is dissolving that should not be dissolving. I blame you entirely. Show me what you have done.",
  "The elements are whispering and they will not tell me what about. Let me see what you brought.",
  "I smelled sulfur before you arrived. That is either a warning or an invitation. I have never learned the difference.",
];

const PROMPTS = [
  "Place them in the vessel. Gently. The lead does not forgive carelessness and neither do I.",
  "Show me what you have gathered. I have seen stranger things than colors. I have dissolved stranger things than colors.",
  "The elements will reveal their nature whether they want to or not. That is the beautiful cruelty of alchemy.",
];

const CLOSINGS = [
  "The residue tells me everything and nothing simultaneously. That is the nature of the work.",
  "Whether you understood any of that is beside the point. The crucible understood. That is what matters.",
  "I would explain further but the mercury is getting ideas again. It does that when left unsupervised. Off you go.",
  "The circle closes. I did not close it. It closed itself. That is either very good or very dangerous.",
  "Do not ask me what it means. I have been doing this for longer than you have been choosing colors and I am no closer to knowing.",
];

// ── Card Data ─────────────────────────────────────────────────
// 3 cards are drawn from the palette's elements for the spread

interface AlchemistCard {
  color: string;
  mapping: ElementMapping;
  arcana: ArcanaMapping;
  label: string; // "Essence" | "Tension" | "Becoming"
}

interface AlchemistContent {
  greeting: string;
  prompt: string;
  cards: AlchemistCard[];
  reading: Reading;
  closing: string;
  colors: string[];
}

const CARD_LABELS = ["Essence", "Tension", "Becoming"] as const;

interface ColorAlchemistProps {
  className?: string;
  /** Colors from a just-submitted moment - triggers the alchemist to appear */
  justSubmittedColors?: string[];
  /** Callback when the alchemist dismisses after a reading */
  onDismiss?: () => void;
}

type AlchemistState =
  | "hidden"
  | "entering"
  | "greeting"
  | "prompt"
  | "phase2"
  | "phase3"
  | "farewell"
  | "closing";

// Preload images
const preloadImages = ["/the_alchemist.png", "/shakespeare-thought-2.png"];
preloadImages.forEach((src) => {
  const img = new Image();
  img.src = src;
});

export function ColorAlchemist({
  className,
  justSubmittedColors,
  onDismiss,
}: ColorAlchemistProps) {
  const [state, setState] = useState<AlchemistState>("hidden");
  const [content, setContent] = useState<AlchemistContent | null>(null);
  const [hasEverShown, setHasEverShown] = useState(false);

  // Greeting/prompt text fade-in
  const [greetingVisible, setGreetingVisible] = useState(false);
  const [promptVisible, setPromptVisible] = useState(false);

  // Phase 2: 3-card spread — staggered flips
  const [flippedCards, setFlippedCards] = useState<boolean[]>([
    false,
    false,
    false,
  ]);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [allFlipped, setAllFlipped] = useState(false);

  // Phase 3: reading reveal
  const [readingVisible, setReadingVisible] = useState(false);

  // Farewell
  const [farewellVisible, setFarewellVisible] = useState(false);

  const [reviewColors, setReviewColors] = useState<string[]>([]);
  const rolledForColorsRef = useRef<string[] | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trigger logic — probability gating handled externally by usePostSubmissionEvent.
  useEffect(() => {
    if (
      justSubmittedColors &&
      justSubmittedColors.length > 0 &&
      state === "hidden"
    ) {
      if (rolledForColorsRef.current === justSubmittedColors) return;
      rolledForColorsRef.current = justSubmittedColors;

      setReviewColors(justSubmittedColors);

      timerRef.current = setTimeout(() => {
        setHasEverShown(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setState("entering");
          });
        });
      }, 1800);
    }
  }, [justSubmittedColors, state, onDismiss]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const generateContent = useCallback((): AlchemistContent => {
    const greeting = randomFrom(GREETINGS);
    const prompt = randomFrom(PROMPTS);
    const closing = randomFrom(CLOSINGS);

    const colorsToUse = reviewColors.length > 0 ? reviewColors : [];

    if (!colorsToUse.length) {
      return {
        greeting,
        prompt,
        cards: [],
        reading: {
          title: "The Void",
          interpretation: "Nothing to transmute. Bring me colors.",
        },
        closing,
        colors: [],
      };
    }

    // Map each color to its element
    const allElements = colorsToUse.map((color) => ({
      color,
      mapping: getElementMapping(color),
    }));

    // Select 3 cards — prefer unique elements, fill from shuffled pool
    const shuffled = [...allElements].sort(() => Math.random() - 0.5);
    const selected: typeof allElements = [];
    const usedElements = new Set<string>();

    // First pass: pick unique elements
    for (const el of shuffled) {
      if (selected.length >= 3) break;
      if (!usedElements.has(el.mapping.element)) {
        selected.push(el);
        usedElements.add(el.mapping.element);
      }
    }
    // Fill remaining slots if < 3 unique
    for (const el of shuffled) {
      if (selected.length >= 3) break;
      if (!selected.includes(el)) {
        selected.push(el);
      }
    }

    const cards: AlchemistCard[] = selected.slice(0, 3).map((el, i) => ({
      color: el.color,
      mapping: el.mapping,
      arcana: getArcanaMapping(el.color),
      label: CARD_LABELS[i],
    }));

    const reading = getCombinationReading(cards.map((c) => c.mapping));

    return { greeting, prompt, cards, reading, closing, colors: colorsToUse };
  }, [reviewColors]);

  // ── Phase transitions ──────────────────────────────────────

  // Greeting fade-in
  useEffect(() => {
    if (state !== "greeting") {
      setGreetingVisible(false);
      return;
    }
    const timer = setTimeout(() => setGreetingVisible(true), 500);
    return () => clearTimeout(timer);
  }, [state]);

  // Prompt fade-in
  useEffect(() => {
    if (state !== "prompt") {
      setPromptVisible(false);
      return;
    }
    const timer = setTimeout(() => setPromptVisible(true), 500);
    return () => clearTimeout(timer);
  }, [state]);

  // Phase 2: show cards, then stagger flips
  useEffect(() => {
    if (state !== "phase2") {
      setCardsVisible(false);
      setFlippedCards([false, false, false]);
      setAllFlipped(false);
      return;
    }

    // Cards fade in
    const showTimer = setTimeout(() => setCardsVisible(true), 300);

    // Stagger flips: 1st at 1.2s, 2nd at 2.0s, 3rd at 2.8s
    const flip1 = setTimeout(
      () => setFlippedCards((p) => [true, p[1], p[2]]),
      1200,
    );
    const flip2 = setTimeout(
      () => setFlippedCards((p) => [p[0], true, p[2]]),
      2000,
    );
    const flip3 = setTimeout(() => {
      setFlippedCards([true, true, true]);
      setTimeout(() => setAllFlipped(true), 400);
    }, 2800);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(flip1);
      clearTimeout(flip2);
      clearTimeout(flip3);
    };
  }, [state]);

  // Phase 3: reading reveal
  useEffect(() => {
    if (state !== "phase3") {
      setReadingVisible(false);
      return;
    }
    const timer = setTimeout(() => setReadingVisible(true), 400);
    return () => clearTimeout(timer);
  }, [state]);

  // Farewell
  useEffect(() => {
    if (state !== "farewell") {
      setFarewellVisible(false);
      return;
    }
    const timer = setTimeout(() => setFarewellVisible(true), 500);
    return () => clearTimeout(timer);
  }, [state]);

  // ── Handlers ───────────────────────────────────────────────

  const handleCharacterClick = useCallback(() => {
    if (state === "entering") {
      setContent(generateContent());
      setState("greeting");
    }
  }, [state, generateContent]);

  const handleAdvance = useCallback(() => {
    if (state === "greeting" && greetingVisible) {
      setState("prompt");
    } else if (state === "prompt" && promptVisible) {
      setState("phase2");
    } else if (state === "phase2" && allFlipped) {
      setState("phase3");
    } else if (state === "phase3" && readingVisible) {
      setState("farewell");
    } else if (state === "farewell" && farewellVisible) {
      setState("closing");
      setTimeout(() => {
        setState("hidden");
        setReviewColors([]);
        onDismiss?.();
      }, 600);
    }
  }, [
    state,
    greetingVisible,
    promptVisible,
    allFlipped,
    readingVisible,
    farewellVisible,
    onDismiss,
  ]);

  const handleBackdropClick = useCallback(() => {
    if (
      state === "greeting" ||
      state === "prompt" ||
      state === "phase2" ||
      state === "phase3" ||
      state === "farewell"
    ) {
      setState("closing");
      setTimeout(() => {
        setState("hidden");
        setReviewColors([]);
        onDismiss?.();
      }, 600);
    }
  }, [state, onDismiss]);

  const isReading =
    state === "greeting" ||
    state === "prompt" ||
    state === "phase2" ||
    state === "phase3" ||
    state === "farewell";

  // Dominant color from the palette for accent
  const accentColor = useMemo(() => {
    if (!reviewColors.length) return "#a78bfa";
    let best = reviewColors[0];
    let bestSat = 0;
    for (const c of reviewColors) {
      const { s } = hexToHsl(c);
      if (s > bestSat) {
        bestSat = s;
        best = c;
      }
    }
    return best;
  }, [reviewColors]);

  // Can the user advance by tapping?
  const canAdvance =
    (state === "greeting" && greetingVisible) ||
    (state === "prompt" && promptVisible) ||
    (state === "phase2" && allFlipped) ||
    (state === "phase3" && readingVisible) ||
    (state === "farewell" && farewellVisible);

  return (
    <>
      {/* Animations */}
      <style>{`
        @keyframes alchemist-glyph-float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
          50% { transform: translateY(-12px) rotate(8deg); opacity: 0.6; }
        }
        .alchemist-glyph {
          animation: alchemist-glyph-float 4s ease-in-out infinite;
          position: absolute;
          pointer-events: none;
          font-size: 1.5rem;
        }

        /* ── 3D Card Flip (Nostrdamus pattern) ── */
        .alch-card {
          perspective: 1500px;
          width: clamp(80px, 22vw, 110px);
          height: clamp(120px, 33vw, 165px);
        }
        .alch-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          transition: transform 0.9s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        .alch-card.flipped .alch-card-inner {
          transform: rotateY(180deg);
        }
        .alch-card-front,
        .alch-card-back-face {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          border-radius: 12px;
          overflow: hidden;
        }
        .alch-card-front {
          transform: rotateY(180deg);
        }

        /* Card back: mystical design */
        .alch-card-back-face {
          background: linear-gradient(135deg, #1a0e2e, #2c1a3d);
          border: 2px solid #4d3a6e;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 0.25rem;
        }

        /* Twinkling stars on card back */
        @keyframes alch-twinkle {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.7; }
        }
        .alch-star {
          position: absolute;
          color: rgba(212, 175, 55, 0.5);
          pointer-events: none;
        }

        /* Card front: colored tarot */
        .alch-card-front-inner {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          border-radius: 12px;
          position: relative;
          overflow: hidden;
        }

        /* Pulsing glow behind icon */
        @keyframes mystical-glow {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
          50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.6; }
        }
        .alch-mystical-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          filter: blur(25px);
          z-index: 1;
          animation: mystical-glow 3s ease-in-out infinite;
        }

        @keyframes pointerPulseAlch {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.1); }
        }
        .pointer-hint-alch {
          animation: pointerPulseAlch 1.5s ease-in-out infinite;
        }

        @keyframes alchJitter {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(0.3px, -0.3px); }
          50% { transform: translate(-0.3px, 0.2px); }
          75% { transform: translate(0.2px, 0.3px); }
        }
        .alch-jitter {
          animation: alchJitter 2.5s ease-in-out infinite;
          display: inline-block;
        }

        /* Spread entrance */
        @keyframes spread-in {
          0% { opacity: 0; transform: translateY(20px) scale(0.9); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .spread-enter {
          animation: spread-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      {/* Backdrop — dark, mystical */}
      <div
        className={cn(
          "fixed inset-0 transition-opacity duration-700 z-[60]",
          isReading ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        style={{
          background: isReading
            ? "radial-gradient(ellipse at center, rgba(30, 10, 60, 0.85) 0%, rgba(0, 0, 0, 0.92) 100%)"
            : "rgba(0,0,0,0.7)",
        }}
        onClick={handleBackdropClick}
      >
        {/* Floating alchemical glyphs */}
        {isReading && (
          <>
            {["☿", "♂", "☽", "☉", "♄", "♀", "♃", "⊕"].map((glyph, i) => (
              <span
                key={i}
                className="alchemist-glyph"
                style={{
                  left: `${10 + ((i * 11) % 80)}%`,
                  top: `${15 + ((i * 17) % 60)}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: `${3 + (i % 3)}s`,
                  fontSize: `${1.2 + (i % 3) * 0.4}rem`,
                  color: accentColor,
                  filter: "blur(0.5px)",
                }}
              >
                {glyph}
              </span>
            ))}
          </>
        )}
      </div>

      {/* Character — reusing critic art for now */}
      {hasEverShown && (
        <div
          className={cn(
            "fixed bottom-0 left-1/2 -translate-x-1/2 z-[80] select-none transition-all duration-700 ease-out",
            state === "hidden" || state === "closing"
              ? "pointer-events-none"
              : "cursor-pointer",
            className,
          )}
          style={{
            width: isReading ? "100%" : undefined,
            maxWidth: isReading ? "32rem" : undefined,
          }}
          onClick={
            state === "hidden" || state === "closing"
              ? undefined
              : isReading
                ? handleAdvance
                : handleCharacterClick
          }
        >
          <div
            className={cn(
              "transition-all duration-700 ease-out flex justify-center",
              state === "hidden" && "translate-y-full opacity-0",
              state === "entering" && "translate-y-[70%]",
              state === "closing" && "translate-y-full opacity-0",
              isReading && "translate-y-[35%]",
            )}
          >
            <img
              src="/the_alchemist.png"
              alt="The Alchemist"
              className={cn(
                "h-auto drop-shadow-2xl transition-all duration-700 ease-out",
                isReading
                  ? "w-full object-cover object-top"
                  : "w-72 sm:w-80 md:w-96 hover:scale-105",
              )}
              draggable={false}
            />
          </div>
        </div>
      )}

      {/* Content area — replaces thought bubble with direct overlay for card spread */}
      {isReading && (
        <div className="fixed inset-x-0 bottom-[30dvh] md:bottom-[28dvh] z-[85] flex justify-center pointer-events-none overflow-visible">
          <div
            className="pointer-events-auto cursor-pointer w-[180vw] max-w-none md:w-full md:max-w-4xl transition-all duration-500 scale-125 md:scale-100"
            onClick={handleAdvance}
          >
            <div className="relative">
              {/* Thought bubble image */}
              <div className="relative w-[130dvw] pb-[85%] md:w-full">
                <img
                  src="/shakespeare-thought-2.png"
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain"
                  style={{
                    filter:
                      "invert(1) sepia(1) saturate(3) hue-rotate(190deg) brightness(0.6)",
                  }}
                  draggable={false}
                />
              </div>

              {/* Content overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center px-[15%] pt-[8%] pb-[25%]">
                <div
                  className="w-full max-w-[70%] h-full flex flex-col items-center justify-center text-center"
                  style={{
                    containerType: "inline-size",
                    overflowWrap: "normal",
                    wordBreak: "normal",
                  }}
                >
                  {/* ── Greeting ── */}
                  {state === "greeting" && content && (
                    <p
                      className={cn(
                        "text-stone-200 font-bold leading-relaxed transition-opacity duration-1000 tracking-wide",
                        greetingVisible ? "opacity-100" : "opacity-0",
                      )}
                      style={{ fontSize: "clamp(1rem, 6cqw, 1.5rem)" }}
                    >
                      {renderAlchJitter(content.greeting)}
                    </p>
                  )}

                  {/* ── Prompt ── */}
                  {state === "prompt" && content && (
                    <p
                      className={cn(
                        "text-stone-200 font-bold leading-relaxed transition-opacity duration-1000 tracking-wide",
                        promptVisible ? "opacity-100" : "opacity-0",
                      )}
                      style={{ fontSize: "clamp(1rem, 6cqw, 1.5rem)" }}
                    >
                      {renderAlchJitter(content.prompt)}
                    </p>
                  )}

                  {/* ── Phase 2: 3-Card Spread ── */}
                  {state === "phase2" &&
                    content &&
                    content.cards.length > 0 && (
                      <div
                        className={cn(
                          "flex items-start justify-center gap-[3vw] transition-opacity duration-600",
                          cardsVisible
                            ? "opacity-100 spread-enter"
                            : "opacity-0",
                        )}
                      >
                        {content.cards.map((card, i) => (
                          <div
                            key={i}
                            className="flex flex-col items-center"
                            style={{ animationDelay: `${i * 0.1}s` }}
                          >
                            {/* Flippable card */}
                            <div
                              className={cn(
                                "alch-card",
                                flippedCards[i] && "flipped",
                              )}
                            >
                              <div className="alch-card-inner">
                                {/* Back (visible initially) */}
                                <div className="alch-card-back-face">
                                  {/* Twinkling stars */}
                                  {[
                                    {
                                      top: "15%",
                                      left: "20%",
                                      size: 10,
                                      dur: 4,
                                      delay: 0,
                                    },
                                    {
                                      top: "30%",
                                      left: "75%",
                                      size: 7,
                                      dur: 5,
                                      delay: 1,
                                    },
                                    {
                                      top: "65%",
                                      left: "15%",
                                      size: 8,
                                      dur: 3.5,
                                      delay: 0.5,
                                    },
                                    {
                                      top: "80%",
                                      left: "80%",
                                      size: 6,
                                      dur: 4.5,
                                      delay: 1.5,
                                    },
                                  ].map((star, si) => (
                                    <span
                                      key={si}
                                      className="alch-star"
                                      style={{
                                        top: star.top,
                                        left: star.left,
                                        fontSize: `${star.size}px`,
                                        animation: `alch-twinkle ${star.dur}s ease-in-out infinite`,
                                        animationDelay: `${star.delay}s`,
                                      }}
                                    >
                                      ✦
                                    </span>
                                  ))}
                                  {/* Central symbol */}
                                  <span
                                    className="text-2xl"
                                    style={{ color: "rgba(212, 175, 55, 0.6)" }}
                                  >
                                    ✧
                                  </span>
                                </div>

                                {/* Front (revealed on flip) — colored card with icon */}
                                <div className="alch-card-front">
                                  <div
                                    className="alch-card-front-inner"
                                    style={{
                                      background: card.color,
                                      border: `2px solid ${card.color}`,
                                    }}
                                  >
                                    <div
                                      className="alch-mystical-glow"
                                      style={{
                                        background: `radial-gradient(circle, rgba(255,255,255,0.5), rgba(255,255,255,0.1))`,
                                      }}
                                    />
                                    <span
                                      className="z-10"
                                      style={{
                                        fontSize: "clamp(3.5rem, 16vw, 7rem)",
                                        color: getSymbolColor(card.color),
                                        opacity: 0.85,
                                        lineHeight: 1,
                                      }}
                                    >
                                      {card.mapping.symbol}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                  {/* ── Phase 3: Divination Reading ── */}
                  {state === "phase3" && content && (
                    <div
                      className={cn(
                        "transition-opacity duration-1000",
                        readingVisible ? "opacity-100" : "opacity-0",
                      )}
                    >
                      {/* Mini cards recap */}
                      <div className="flex justify-center gap-[2cqw] mb-[3cqw]">
                        {content.cards.map((card, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-center"
                            style={{
                              width: "clamp(28px, 10cqw, 44px)",
                              height: "clamp(42px, 15cqw, 66px)",
                              backgroundColor: card.color,
                              borderRadius: "3px",
                              border: `1px solid ${card.color}`,
                              boxShadow: `0 2px 6px rgba(0,0,0,0.3)`,
                            }}
                          >
                            <span
                              style={{
                                fontSize: "clamp(0.8rem, 5cqw, 1.4rem)",
                                color: getSymbolColor(card.color),
                                opacity: 0.85,
                                lineHeight: 1,
                              }}
                            >
                              {card.mapping.symbol}
                            </span>
                          </div>
                        ))}
                      </div>
                      <p
                        className="font-bold tracking-wider text-stone-100 mb-[1.5cqw]"
                        style={{ fontSize: "clamp(1.1rem, 6cqw, 1.6rem)" }}
                      >
                        {renderAlchJitter(content.reading.title)}
                      </p>
                      <p
                        className="text-stone-300 leading-relaxed max-w-[90%] mx-auto"
                        style={{ fontSize: "clamp(0.8rem, 4cqw, 1.1rem)" }}
                      >
                        {renderAlchJitter(content.reading.interpretation)}
                      </p>
                    </div>
                  )}

                  {/* ── Farewell ── */}
                  {state === "farewell" && content && (
                    <p
                      className={cn(
                        "text-stone-200 font-bold leading-relaxed transition-opacity duration-1000 tracking-wide",
                        farewellVisible ? "opacity-100" : "opacity-0",
                      )}
                      style={{ fontSize: "clamp(1rem, 6cqw, 1.5rem)" }}
                    >
                      {renderAlchJitter(content.closing)}
                    </p>
                  )}

                  {/* Pointer hint — shows when user can advance */}
                  <div
                    className={cn(
                      "mt-3 transition-opacity duration-300",
                      canAdvance ? "opacity-100" : "opacity-0",
                    )}
                  >
                    <span
                      className="text-stone-400 pointer-hint-alch inline-block"
                      style={{ fontSize: "clamp(1rem, 3vw, 1.25rem)" }}
                    >
                      ✧
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function renderAlchJitter(text: string | undefined | null) {
  if (!text) return null;
  const words = text.split(" ");
  return (
    <span>
      {words.map((word, i) => (
        <span key={i}>
          <span
            className="alch-jitter"
            style={{
              animationDelay: `${i * 0.08}s`,
              animationDuration: `${2 + (i % 4) * 0.3}s`,
            }}
          >
            {word}
          </span>
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
}
